/**
 * Props that tell password managers to leave a field alone.
 *
 * Operator report 2026-08-16: LastPass popped its icon over the bottom-nav
 * project-title field and over the Stage 1 "Quote requester" trio. Those three
 * (name + email + job title, adjacent, in one group) look exactly like a
 * signup form to a credential manager's heuristics — but they hold the
 * CLIENT's details, never an account credential.
 *
 * `autoComplete="off"` alone is not enough: Chrome ignores it on fields it has
 * classified as identity, and each manager reads its own opt-out attribute.
 * Spread this object onto any input that holds business data rather than a
 * credential:
 *
 *   <input {...NO_PASSWORD_MANAGER} value={…} onChange={…} />
 *
 * Deliberately NOT applied to the real sign-in fields (LoginPage, the
 * accept-invitation password fields) — those SHOULD be offered to a manager.
 */
export const NO_PASSWORD_MANAGER = {
    // A NONSTANDARD token, deliberately: Chrome ignores `off` on fields it has
    // classified as identity, but bails on tokens it does not recognise, and
    // the managers' own heuristics read the attribute too. (Operator re-report
    // 2026-08-19: LastPass still iconed the title field WITH data-lpignore set,
    // so the attribute alone is not sufficient for current LastPass — fields
    // also need a name/id that reads as non-credential, e.g. the bottom-nav
    // title input's name="qm-heading-field".)
    autoComplete: 'qm-no-autofill',
    'data-lpignore': 'true',      // LastPass
    'data-1p-ignore': 'true',     // 1Password
    'data-bwignore': 'true',      // Bitwarden
    'data-form-type': 'other',    // Dashlane
} as const;

export const NO_PASSWORD_MANAGER_FORM = {
    autoComplete: 'off',
    'data-lpignore': 'true',
    'data-1p-ignore': 'true',
    'data-bwignore': 'true',
    'data-form-type': 'other',
} as const;

/**
 * Business text fields that LastPass still classifies as contact/login fields.
 * Use the existing search-input exclusion without presenting search semantics
 * or search chrome to the operator. Email recipients keep inputMode="email".
 */
export const NO_PASSWORD_MANAGER_TEXT_INPUT = {
    ...NO_PASSWORD_MANAGER,
    type: 'search',
    role: 'textbox',
    'data-qm-business-text': 'true',
} as const;

export const PASSWORD_MANAGER_ALLOW_ATTRIBUTE = 'data-qm-password-manager';
export const PASSWORD_MANAGER_ALLOW_VALUE = 'allow';

const OPERATIONAL_FIELD_SELECTOR = [
    'input:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="checkbox"]):not([type="radio"]):not([type="file"]):not([type="range"]):not([type="color"])',
    'textarea',
    '[contenteditable="true"]',
].join(',');

function isPasswordManagerAllowed(element: Element): boolean {
    return !!element.closest(
        `[${PASSWORD_MANAGER_ALLOW_ATTRIBUTE}="${PASSWORD_MANAGER_ALLOW_VALUE}"]`,
    );
}

function setDomAttributes(
    element: Element,
    attributes: Record<string, string>,
): boolean {
    let changed = false;
    for (const [name, value] of Object.entries(attributes)) {
        const domName = name === 'autoComplete' ? 'autocomplete' : name;
        if (element.getAttribute(domName) === value) continue;
        element.setAttribute(domName, value);
        changed = true;
    }
    return changed;
}

/**
 * Marks authenticated operational forms and fields as business data rather
 * than credentials. Real credential forms opt out with
 * `data-qm-password-manager="allow"` and are left untouched.
 *
 * This is deliberately idempotent so it can be run at mount and for dynamic
 * React content observed later by OperationalFieldBoundary.
 */
export function suppressPasswordManagersInOperationalTree(root: ParentNode): number {
    if (typeof Element === 'undefined') return 0;
    let changedCount = 0;

    const forms: Element[] = [];
    if (root instanceof Element && root.matches('form')) forms.push(root);
    forms.push(...Array.from(root.querySelectorAll('form')));
    for (const form of forms) {
        if (isPasswordManagerAllowed(form)) continue;
        if (setDomAttributes(form, NO_PASSWORD_MANAGER_FORM)) changedCount += 1;
    }

    const fields: Element[] = [];
    if (root instanceof Element && root.matches(OPERATIONAL_FIELD_SELECTOR)) fields.push(root);
    fields.push(...Array.from(root.querySelectorAll(OPERATIONAL_FIELD_SELECTOR)));
    for (const field of fields) {
        if (isPasswordManagerAllowed(field)) continue;
        if (setDomAttributes(field, NO_PASSWORD_MANAGER)) changedCount += 1;
    }

    return changedCount;
}
